import { BASE_URLS } from '@components/sources/constants';
import { apiAuthCaller } from '@services/apiCaller';
import useStores from '@stores/useStores';
import { useState, useEffect } from 'react';
import { SmartRetry } from '@utils/retry';
import { getApiErrorMessage } from '@components/common/util/util';
import { JSONSchema } from '@components/common/jsonSchemaForm/jsonSchemaForm';
import { Catalog } from './singerResourceSelection';
import { AdvancedAuth, AuthSpec } from './utils';

interface SpecResponse {
  connectionSpecification: JSONSchema;
  authSpecification?: AuthSpec;
  advanced_auth?: AdvancedAuth;
}

export const useResources = (options: {
  image: string | undefined;
  config: unknown;
  defaultCatalog?: Catalog[];
  sourceDefinitionName?: string;
  configJSONSchema?: JSONSchema;
}) => {
  const { image, config, configJSONSchema, sourceDefinitionName, defaultCatalog } = options;
  const retry = new SmartRetry<{ data: { streams: Catalog[] } }>({ times: 2 });
  const [catalog, setCatalog] = useState<Catalog[]>(defaultCatalog || []);
  const [loading, setLoading] = useState(false);
  const { userStore, messagesStore } = useStores();

  useEffect(() => {
    if (!config || catalog.length > 0) {
      return;
    }
    setLoading(true);
    retry
      .exec(() =>
        apiAuthCaller(userStore.token).post(`${BASE_URLS.SOURCES_INFO}/rudder_custom/discover`, {
          image,
          config,
          role: sourceDefinitionName,
          configJSONSchema,
        }),
      )
      .then((resp) => {
        const { data } = resp;
        setCatalog(data?.streams || []);
      })
      .catch((e) => {
        messagesStore.showErrorMessage(getApiErrorMessage(e, 'Failed to load spec'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {
    loading,
    catalog,
  };
};

export const useConfigSchema = (image: string, defaultConfigSchema?: Partial<SpecResponse>) => {
  const [specSchema, setSpec] = useState<Partial<SpecResponse>>(defaultConfigSchema || {});
  const [loading, setLoading] = useState(false);
  const { userStore, messagesStore } = useStores();
  const retry = new SmartRetry<{ data: SpecResponse }>({ times: 2 });
  useEffect(() => {
    if (
      specSchema.connectionSpecification &&
      (specSchema.authSpecification || specSchema.advanced_auth)
    ) {
      return;
    }
    setLoading(true);
    retry
      .exec(() =>
        apiAuthCaller(userStore.token, false, 40000).post(
          `${BASE_URLS.SOURCES_INFO}/rudder_custom/spec`,
          {
            image,
          },
        ),
      )
      .then((response) => {
        const spec = response.data as SpecResponse;
        // ignore title returned by the spec command
        spec.connectionSpecification.title = '';
        setSpec(spec);
      })
      .catch((e) => {
        messagesStore.showErrorMessage(getApiErrorMessage(e, 'Failed to load spec'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  return {
    loading,
    connectionSpecification: specSchema.connectionSpecification!,
    authSpec: specSchema.advanced_auth || specSchema.authSpecification,
  };
};
